import React, { useState } from 'react';
import cashReservesService from '../../services/cashReservesService';

/**
 * HiddenSuggestionsModal — the shared list of per-account hidden suggestion
 * types, presented like the dashboard's other management modals (Reserve
 * Creation Queue, Tamarac Todo List).
 *
 * (account, type) pairs land here via the Ignore button on a Suggested
 * Actions row; restoring one brings that account's alerts of the type back on
 * the next load — the backend drops ignored pairs at serve time, so this list
 * is the single place to see and undo what's hidden.
 */
const ignoreKey = (ignore) => `${ignore.account_number}|${ignore.action}`;

const HiddenSuggestionsModal = ({ ignores = [], onClose, onChanged }) => {
  const [busyKey, setBusyKey] = useState('');
  const [error, setError] = useState('');

  const handleRestore = async (ignore) => {
    setBusyKey(ignoreKey(ignore));
    setError('');
    try {
      await cashReservesService.removeAlertIgnore(ignore.action, ignore.account_number);
      if (onChanged) onChanged();
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Failed to restore the suggestion type');
    } finally {
      setBusyKey('');
    }
  };

  return (
    <div className="cash-reserve-modal-backdrop" onClick={onClose} role="presentation">
      <div
        className="card shadow"
        style={{ maxWidth: '900px', width: '100%', maxHeight: '85vh' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="card-header d-flex justify-content-between align-items-center">
          <span className="fw-semibold">
            <i className="bi bi-eye-slash me-2 text-primary"></i>
            Hidden Suggestions
            {ignores.length > 0 && (
              <span className="badge bg-primary ms-2">{ignores.length}</span>
            )}
          </span>
          <button type="button" className="btn-close" aria-label="Close" onClick={onClose}></button>
        </div>
        {/* minHeight: 0 lets the body shrink below its content (a flex child's
            default min-height is auto), so the card's 85vh cap actually holds
            and the table scrolls instead of spilling out of the modal. */}
        <div className="card-body d-flex flex-column" style={{ minHeight: 0 }}>
          <p className="text-muted small">
            Suggestion types hidden for a specific account, for everyone — via the Ignore button
            on a suggested action. Restoring a row brings that account&apos;s suggestions of the
            type back on the next load. Other accounts are never affected either way.
          </p>

          {error && (
            <div className="alert alert-danger py-2 small" role="alert">
              <i className="bi bi-exclamation-triangle-fill me-2"></i>
              {error}
            </div>
          )}

          {ignores.length === 0 ? (
            <div className="text-center py-3 text-muted">
              <i className="bi bi-inbox me-2"></i>
              No suggestion types are hidden.
            </div>
          ) : (
            <div className="table-responsive" style={{ overflowY: 'auto', minHeight: 0 }}>
              <table className="table table-sm align-middle mb-0">
                <thead
                  className="table-light"
                  style={{ position: 'sticky', top: 0, zIndex: 2 }}
                >
                  <tr>
                    <th>Suggestion</th>
                    <th>Account</th>
                    <th>Hidden</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {ignores.map((ignore) => (
                    <tr key={ignoreKey(ignore)}>
                      <td className="fw-semibold">{ignore.label || ignore.action}</td>
                      <td>
                        <div>{ignore.account_name || ignore.account_number}</div>
                        {ignore.account_name && (
                          <div className="small text-muted">{ignore.account_number}</div>
                        )}
                      </td>
                      <td>
                        <div className="small text-muted">
                          {ignore.ignored_by || 'unknown'}
                          {ignore.ignored_at
                            ? ` · ${new Date(ignore.ignored_at).toLocaleDateString()}`
                            : ''}
                        </div>
                      </td>
                      <td className="text-end">
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-primary"
                          disabled={busyKey === ignoreKey(ignore)}
                          onClick={() => handleRestore(ignore)}
                          title="Show this suggestion type again for this account"
                        >
                          {busyKey === ignoreKey(ignore) ? 'Restoring…' : 'Restore'}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default HiddenSuggestionsModal;
